/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import memoize from "lodash.memoize";
import { styled } from "@iventis/styles";
import * as d3 from "d3";
import { combineLatest } from "rxjs";
import { filter } from "rxjs/operators";
import { ScaleTime } from "d3";
import { fontSizes } from "@iventis/styles/src/atomic-rules";
import { getContrastTextColour } from "@iventis/utilities";
import { Session, SessionComponent } from "./types";
import { CalendarCursor, ContextPropsData, SESSION_RECT_PADDING } from "./calendar-context";
import { GroupScale } from "./group-axis";
import { useCursor, useGroupScale, usePropsStream, useTimeScale, useUpdateCursor } from "./hooks";
import { EventUnitComponent } from "./event-unit";

const SESSION_CODE_TAG = "session-code-tag";
const SESSION_CODE_TAG_PADDING = 8;
const SESSION_MAIN_BOX = "session-bg-rec";
const SESSION_START_TIME = "session-start-time";
const SESSION_END_TIME = "session-end-time";
const BORDER_RADIUS = 4;
interface SessionDetailedProps {
    session: Session;
    sessionComponent: SessionComponent;
    eventUnitComponent: SessionComponent;
    sessionTabFormatter?: (session: Session) => string;
    onClick?: (session: Session, event: PointerEvent) => void;
    onContextClick?: (session: Session, event: PointerEvent) => void;
}

export const SessionDetailed: React.FC<SessionDetailedProps> = ({ session, onClick, onContextClick, eventUnitComponent, sessionTabFormatter }) => {
    const ref = useRef<SVGGElement>();
    const bgColour = session.colour.fill;
    const textColour = useMemo(() => getContrastTextColour(bgColour), [bgColour]);
    const props$ = usePropsStream();
    const timeScale$ = useTimeScale();
    const groupScale$ = useGroupScale();
    const cursor$ = useCursor();
    const updateCursor = useUpdateCursor();
    // eslint-disable-next-line no-undef
    const [startTime, setStartTime] = useState<Date>();

    const clickHandler = useCallback(
        (event: React.MouseEvent<SVGGElement, Event>) => {
            onClick?.(session, event.nativeEvent as PointerEvent);
            event.stopPropagation();
        },
        [session, onClick]
    );

    const contextClickHandler = useCallback(
        (event) => {
            onContextClick?.(session, event.nativeEvent);
            event.preventDefault();
            event.stopPropagation();
        },
        [session, onContextClick]
    );

    useEffect(() => {
        const sub = combineLatest([
            timeScale$.pipe(filter<ScaleTime<number, number, never>>(Boolean)),
            groupScale$.pipe(filter<GroupScale>(Boolean)),
            props$.pipe(filter<ContextPropsData>(Boolean)),
        ]).subscribe(([timeScale, groupScale]) => {
            const start = session.sessionStart;
            const end = session.sessionEnd;
            setStartTime(start);

            // position and resize session main rect
            const svg = d3.select(ref.current!);
            const xRange = groupScale.getGroupCoords(session.groupId);
            const y = timeScale(start);
            const width = Math.max(xRange[1] - xRange[0] - 2 * SESSION_RECT_PADDING, 0);
            const height = Math.abs(timeScale(end) - y);
            svg.attr("transform", `translate(${xRange[0]}, ${y})`);
            svg.select(`rect.${SESSION_MAIN_BOX}`).attr("width", width).attr("height", height);

            // position and resize session code tag text
            const textBBox = memoizedBBox(svg.select<SVGTextElement>(`text.${SESSION_CODE_TAG}`).node()!);
            svg.select(`text.${SESSION_CODE_TAG}`)
                .attr("x", width - textBBox.width - SESSION_RECT_PADDING)
                .attr("y", -SESSION_RECT_PADDING);

            // position session end time
            const endTimeHeight = memoizedBBox(svg.select(`text.${SESSION_END_TIME}`).node() as SVGGraphicsElement).height;
            svg.select(`text.${SESSION_END_TIME}`)
                .attr("x", 2 * SESSION_RECT_PADDING)
                .attr("y", height + endTimeHeight - SESSION_RECT_PADDING);

            // position and resize session code tag background rect
            svg.select(`rect.${SESSION_CODE_TAG}`)
                .attr("transform", `translate(${width - textBBox.width - 2 * SESSION_CODE_TAG_PADDING}, ${-textBBox.height - SESSION_CODE_TAG_PADDING + BORDER_RADIUS})`)
                .attr("height", textBBox.height + SESSION_CODE_TAG_PADDING)
                .attr("width", textBBox.width + 2 * SESSION_CODE_TAG_PADDING);

            // draw border
            svg.select("path").attr(
                "d",
                `
                    M${SESSION_RECT_PADDING + BORDER_RADIUS},0
                    H${width - textBBox.width - 2 * SESSION_CODE_TAG_PADDING + BORDER_RADIUS}
                    v-${textBBox.height + SESSION_CODE_TAG_PADDING - 2 * BORDER_RADIUS}
                    a${BORDER_RADIUS},${BORDER_RADIUS},90,0,1,${BORDER_RADIUS},${-BORDER_RADIUS}
                    h${textBBox.width + 2 * SESSION_CODE_TAG_PADDING - 2 * BORDER_RADIUS}
                    a${BORDER_RADIUS},${BORDER_RADIUS},90,0,1,${BORDER_RADIUS},${BORDER_RADIUS}
                    v${height + textBBox.height - BORDER_RADIUS}
                    a${BORDER_RADIUS},${BORDER_RADIUS},90,0,1,${-BORDER_RADIUS},${BORDER_RADIUS}
                    H${SESSION_RECT_PADDING + BORDER_RADIUS}
                    a${BORDER_RADIUS},${BORDER_RADIUS},90,0,1,${-BORDER_RADIUS},${-BORDER_RADIUS}
                    V${BORDER_RADIUS}
                    a${BORDER_RADIUS},${BORDER_RADIUS},90,0,1,${BORDER_RADIUS},${-BORDER_RADIUS}
                `
            );
        });

        return () => sub.unsubscribe();
    }, [timeScale$, groupScale$, props$, session, cursor$]);

    useEffect(() => {
        const sub = cursor$.subscribe((cursor) => {
            d3.select(ref.current!).style("cursor", cursor);
        });
        return () => sub.unsubscribe();
    }, [cursor$]);

    // when the mouse moves on this SessionContainer attempt to change cursor
    // wanted to use onMouseOver but would not emit an event if the panning finished on the same SessionContainer it started on
    const handleOnMouseMove = useCallback(() => {
        updateCursor(CalendarCursor.POINTER);
    }, [updateCursor]);

    // when the mouse leaves the SessionContainer
    const handleMouseLeave = useCallback(() => {
        updateCursor(CalendarCursor.GRAB);
    }, [updateCursor]);

    return (
        <SessionContainer
            ref={ref as React.MutableRefObject<SVGGElement>}
            onClick={clickHandler}
            onContextMenu={contextClickHandler}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleOnMouseMove}
        >
            <rect fill={bgColour} className={SESSION_MAIN_BOX} rx={BORDER_RADIUS} x={SESSION_RECT_PADDING} y={0} />
            <rect fill={bgColour} className={SESSION_CODE_TAG} rx={BORDER_RADIUS} x={SESSION_RECT_PADDING} y={0} />
            <SessionCodeTag fill={textColour} className={SESSION_CODE_TAG}>
                {sessionTabFormatter?.(session)}
            </SessionCodeTag>
            <text x={2 * SESSION_RECT_PADDING} y={-SESSION_RECT_PADDING} className={SESSION_START_TIME}>
                {startTime ? format(startTime, "HH:mm") : ""}
            </text>
            <text className={SESSION_END_TIME}>{format(session.sessionEnd, "HH:mm")}</text>
            <path fill="none" stroke={session.colour.outline} d="" />
            {session.subEvents!.map((subEvent) => (
                <EventUnitComponent
                    eventUnitComponent={eventUnitComponent}
                    onClick={onClick!}
                    subEvent={subEvent}
                    session={session}
                    key={subEvent.id}
                    bgColour={bgColour}
                    textColour={textColour}
                />
            ))}
        </SessionContainer>
    );
};

const SessionCodeTag = styled.text`
    color: ${(props) => props.color || "#000"};
    font-size: ${fontSizes.xSmall};
`;

const SessionContainer = styled.g`
    &:hover {
        rect {
            filter: url(#dropshadow);
        }

        path {
            stroke-width: 2;
        }
    }
`;

const memoizedBBox = memoize((node: SVGGraphicsElement) => node.getBBox());
